import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import {useEffect, useState} from "react";
import actionLogs from '../../models/action-logs'
import helpers from "../../helpers/common";
import useRootHook from "../../hooks/useRoot";
import {Chip, Tooltip, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button} from "@mui/material";
import Pagination from "../Pagination";
import transactions from "../../models/transactions";
import {Error as ErrorIcon, Warning as WarningIcon} from "@mui/icons-material";

export default function ActionLogsIndex({fullPage, searchParams, setSearchParams, refreshId}) {
    fullPage = typeof fullPage === 'boolean' ? fullPage : true
    const [logsData, setLogsData] = useState(null)
    const [detailsLog, setDetailsLog] = useState(null)
    const {setShowLoader} = useRootHook()

    useEffect(() => {
        setShowLoader(true)
        let baseFilters = {}
        if (searchParams) {
            baseFilters = Object.fromEntries(searchParams)
        }
        actionLogs.getData(baseFilters)
            .then((data) => {
                if (!data) {
                    setShowLoader(false)
                    return
                }
                data && setLogsData(data)
                setShowLoader(false)
            }).catch((err) => {
                console.error('Error loading action logs:', err)
                setShowLoader(false)
        })
    }, [searchParams, refreshId, setShowLoader])

    if (!logsData) return null

    const getStatusChip = (log) => {
        if (log.action_type === 'error') {
            const errorType = log.error_type || 'unknown_error'
            const errorMessages = {
                'already_paid': 'Уже оплачено',
                'limit_exceeded': 'Превышен лимит',
                'currency_not_available': 'Валюта недоступна',
                'no_payment_methods': 'Нет методов оплаты',
                'invalid_amount': 'Неверная сумма',
                'link_expired': 'Ссылка истекла',
                'payments_disabled': 'Платежи отключены',
                'unknown_error': 'Неизвестная ошибка'
            }
            const color = errorType === 'already_paid' ? 'warning' : 'error'
            return (
                <Chip 
                    size="small" 
                    color={color} 
                    label={errorMessages[errorType] || errorMessages['unknown_error']}
                    icon={color === 'warning' ? <WarningIcon /> : <ErrorIcon />}
                />
            )
        }
        
        if (log.status) {
            return (
                <Chip 
                    size="small" 
                    color={transactions.getChipStatusColor(log.status)} 
                    label={transactions.enumToStatus[log.status]?.capitalize() || log.status}
                />
            )
        }
        
        return <Chip size="small" label="-" />
    }

    const getStripeInfo = (log) => {
        if (log.action_type === 'error') {
            return log.paymethod?.paysystem?.key === 'stripe' ? log.paymethod?.stripe_tag || '-' : '-'
        }
        
        // Для транзакций Stripe: отображаем ID метода оплаты и provider_id (payment intent ID)
        if (log.paymethod?.paysystem?.key === 'stripe') {
            const paymethodId = log.paymethod?.id || ''
            const providerId = log.provider_id || log.stripe_info?.provider_id || ''
            if (paymethodId && providerId) {
                return `${paymethodId} ${providerId}`
            } else if (providerId) {
                return providerId
            } else if (paymethodId) {
                return paymethodId
            }
        }
        
        return '-'
    }

    const getAmount = (log) => {
        if (!log.amount || !log.currency) return '-'
        const currency = log.currency
        const subunit = currency.subunit || 1
        const symbol = currency.symbol || ''
        return helpers.getSubunitAmountString(log.amount, symbol, subunit)
    }

    const getCustomerInfo = (log) => {
        if (log.customer) {
            const email = log.customer.email || '-'
            const name = log.customer.name && log.customer.surname 
                ? `${log.customer.name} ${log.customer.surname}` 
                : (log.customer.name || '')
            return name ? `${email} (${name})` : email
        }
        return '-'
    }

    const getLimitInfo = (log) => {
        if (log.action_type === 'error') return '-'
        if (!log.daily_limit_info) return '-'
        
        const info = log.daily_limit_info
        const currency = log.currency || {}
        const subunit = currency.subunit || 1
        const symbol = currency.symbol || ''
        
        const max = helpers.getSubunitAmountString(info.max_day_total, symbol, subunit)
        const used = helpers.getSubunitAmountString(info.day_total, symbol, subunit)
        const remaining = helpers.getSubunitAmountString(info.remaining, symbol, subunit)
        
        return `Лимит: ${max} | Зашло: ${used} | Осталось: ${remaining}`
    }

    const getDetailsPayload = (log) => {
        if (log.action_type === 'error') {
            return {
                error_type: log.error_type,
                error_message: log.error_message,
                ip: log.ip_address,
                ua: log.user_agent
            }
        }
        return null
    }

    return (
        <React.Fragment>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Дата создания</TableCell>
                        <TableCell>Мерчант</TableCell>
                        <TableCell>Проект</TableCell>
                        <TableCell>Клиент</TableCell>
                        <TableCell>Платежная система</TableCell>
                        <TableCell>Метод оплаты</TableCell>
                        <TableCell>Stripe</TableCell>
                        <TableCell>Сумма</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Лимиты</TableCell>
                        <TableCell>Details</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {logsData.logs.map((log) => {
                        const isError = log.action_type === 'error'
                        return (
                            <TableRow key={`log-${log.id}`}>
                                <TableCell>
                                    {isError ? (
                                        <Tooltip title="Ошибка">
                                            <ErrorIcon color="error" fontSize="small" />
                                        </Tooltip>
                                    ) : (
                                        log.id
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{fontWeight: 'bold'}}>
                                        {helpers.getTime(log.created)}
                                    </Typography>
                                    <Typography variant="body2">
                                        {helpers.getDate(log.created)}
                                    </Typography>
                                </TableCell>
                                <TableCell>{log.merchant?.name || '-'}</TableCell>
                                <TableCell>{log.project?.name || '-'}</TableCell>
                                <TableCell>{getCustomerInfo(log)}</TableCell>
                                <TableCell>
                                    {log.paymethod?.paysystem?.key ? log.paymethod.paysystem.key.toUpperCase() : '-'}
                                </TableCell>
                                <TableCell>
                                    {log.paymethod?.name ? `${log.paymethod.name}${log.paymethod.id ? ` (${log.paymethod.id})` : ''}` : '-'}
                                </TableCell>
                                <TableCell>{getStripeInfo(log)}</TableCell>
                                <TableCell>{getAmount(log)}</TableCell>
                                <TableCell>{getStatusChip(log)}</TableCell>
                                <TableCell>
                                    <Typography variant="caption" sx={{fontSize: '0.7rem'}}>
                                        {getLimitInfo(log)}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    {log.action_type === 'error' ? (
                                        <Button size="small" variant="outlined" onClick={() => setDetailsLog(log)}>
                                            View
                                        </Button>
                                    ) : (
                                        '-'
                                    )}
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {fullPage && <Pagination url={'/action-logs'} count={logsData.count}/>}
            <Dialog open={!!detailsLog} onClose={() => setDetailsLog(null)} maxWidth="md" fullWidth>
                <DialogTitle>Log Details</DialogTitle>
                <DialogContent>
                    <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                        {detailsLog ? JSON.stringify(getDetailsPayload(detailsLog), null, 2) : ''}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsLog(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

